/**
 * LoginPageV3 — Minimal Enterprise Style
 * Left (4 cols): Hero branding + feature blocks + certifications
 * Center (5 cols): Clean form + SSO options
 * Right (3 cols): Status board + quick info
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Eye, EyeOff, User, Lock, ChevronRight, ArrowRight,
  Zap, Shield, Globe, BarChart3, CheckCircle2,
  Server, Wifi, Database, CircleDot, Clock, Users
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';

import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useGlobalization } from '../context/GlobalizationContext';
import RetailCliqLogo from '../components/RetailCliqLogo';
import ThemeToggle from '../components/ThemeToggle';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import FloatingParticles from '../components/FloatingParticles';
import { FormHelp } from '../../components/help-engine/FormHelp';
import { API_BASE, API } from '../../lib/api';


const FEATURES_V3 = [
  { icon: Zap, title: 'AI-Powered Insights', desc: 'Real-time analytics and demand forecasting powered by machine learning.' },
  { icon: Shield, title: 'Enterprise Security', desc: 'MFA, SSO, audit logs and ISO 27001 compliance built-in.' },
  { icon: Globe, title: 'Multi-Branch & Multi-Currency', desc: 'Manage 2500+ stores across geographies from one dashboard.' },
  { icon: BarChart3, title: 'Unified Reporting', desc: 'Finance, inventory, HR and POS reports in a single platform.' },
];

const CERTS = ['ISO 27001', 'PCI-DSS', 'GST Ready', 'MFA', 'SOC 2'];

const STATUS_SERVICES = [
  { name: 'API', icon: Server, status: 'up', latency: '12ms' },
  { name: 'DB', icon: Database, status: 'up', latency: '4ms' },
  { name: 'Network', icon: Wifi, status: 'up', latency: '8ms' },
  { name: 'Auth', icon: CircleDot, status: 'up', latency: '6ms' },
];

export default function LoginPageV3() {
  const { loginWithTemp, completeLogin, accessToken } = useAuth();
  const { isDark } = useTheme();
  const { t, formatTime } = useGlobalization();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [time, setTime] = useState(new Date());
  const [activeUsers] = useState(Math.floor(Math.random() * 50) + 120);

  useEffect(() => { if (accessToken) navigate('/select-app', { replace: true }); }, [accessToken, navigate]);
  useEffect(() => { const id = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(id); }, []);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) { toast.error('Please enter credentials'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username: username.trim(), password, remember_me: rememberMe });
      toast.success(`Welcome, ${data.user_summary.name}!`);
      if (data.requiresMfa) { loginWithTemp(data.temp_token, data.user_summary, rememberMe); navigate('/mfa'); }
      else { completeLogin(data.access_token, data.user_summary, rememberMe); navigate('/select-app'); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed.');
    } finally { setLoading(false); }
  }, [username, password, rememberMe, loginWithTemp, completeLogin, navigate]);

  return (
    <div className="min-h-screen relative overflow-hidden dark:bg-[#080808] bg-slate-50 pb-10" data-testid="login-page-v3">
      {isDark && <FloatingParticles />}

      <div className="relative z-10 min-h-screen grid grid-cols-12">

        {/* LEFT — Hero Branding (4 cols) */}
        <div className="hidden lg:flex lg:col-span-4 flex-col p-3 relative overflow-hidden lg:min-h-screen dark:bg-[#0f0f0f] bg-white">
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(500px circle at 0% 30%, rgba(61,213,109,0.12), transparent 55%),radial-gradient(400px circle at 110% 80%, rgba(61,213,109,0.07), transparent 55%)' }} />
          <div className="relative z-10 flex flex-col h-full">

            <div className="mb-6">
              <RetailCliqLogo size="md" showTagline />
            </div>

            <div className="mb-6">
              <h1 className="text-3xl font-bold dark:text-white text-gray-900 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Smart Operations<br /><span className="text-[#3DD56D]">Start Here</span>
              </h1>
              <p className="dark:text-neutral-400 text-gray-500 text-sm mt-2 leading-relaxed">
                The complete ERP platform for modern retail businesses.
              </p>
            </div>

            {/* Feature blocks */}
            <div className="space-y-3 flex-1">
              {FEATURES_V3.map((f, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.08 }}
                  className="flex gap-3">
                  <div className="w-8 h-8 rounded-xl bg-[#3DD56D]/10 border border-[#3DD56D]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <f.icon size={15} className="text-[#3DD56D]" />
                  </div>
                  <div>
                    <p className="dark:text-white text-gray-900 text-sm font-semibold leading-snug">{f.title}</p>
                    <p className="dark:text-neutral-500 text-gray-500 text-xs mt-0.5 leading-relaxed">{f.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Certifications */}
            <div className="mt-4 flex flex-wrap gap-1.5">
              {CERTS.map(c => (
                <span key={c} className="text-[10px] dark:bg-white/[0.05] bg-gray-100 dark:border-white/[0.08] border-gray-200 border px-2 py-0.5 rounded-full dark:text-neutral-400 text-gray-500">
                  {c}
                </span>
              ))}
            </div>

            <div className="dark:border-t dark:border-white/[0.06] border-t border-gray-100 pt-3 mt-3">
              <p className="dark:text-neutral-700 text-gray-400 text-[10px]">© 2025 RetailCliQ Corp. All rights reserved.</p>
            </div>
          </div>
        </div>

        {/* CENTER — Clean Form (5 cols) */}
        <div className="col-span-12 md:col-span-8 lg:col-span-5 flex items-center justify-center p-3 lg:p-5 relative">
          <div className="absolute top-3 right-3 z-20"><ThemeToggle /></div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.38 }}
            className="w-full max-w-[380px]">
            <div className="lg:hidden flex justify-center mb-4"><RetailCliqLogo size="sm" showTagline /></div>

            <div className="dark:bg-white/[0.04] bg-white dark:border-white/[0.09] border-gray-200 border dark:shadow-[0_20px_50px_rgba(0,0,0,0.5)] shadow-lg rounded-2xl p-5">
              <div className="mb-5">
                <h2 className="text-xl font-bold dark:text-white text-gray-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {t('welcomeBack')}
                </h2>
                <p className="dark:text-neutral-400 text-gray-500 text-xs mt-1">{t('signInToAccount')}</p>
              </div>

              <FormHelp formName="Login">
              <form onSubmit={handleLogin} className="space-y-3" data-dev-component="LoginPortalV3">
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" size={14} />
                  <Input data-testid="login-username-input" data-help-control="username" type="text" placeholder={t('usernamePlaceholder')} value={username}
                    onChange={e => setUsername(e.target.value)} autoComplete="username"
                    className="pl-9 h-9 rounded-lg dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.09] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 placeholder:text-gray-400 focus-visible:ring-[#3DD56D] focus-visible:ring-1 text-sm" />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" size={14} />
                  <Input data-testid="login-password-input" data-help-control="password" type={showPassword ? 'text' : 'password'} placeholder={t('passwordPlaceholder')}
                    value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password"
                    className="pl-9 pr-9 h-9 rounded-lg dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.09] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 placeholder:text-gray-400 focus-visible:ring-[#3DD56D] focus-visible:ring-1 text-sm" />
                  <button type="button" onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400 hover:text-gray-600 transition-colors">
                    {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Checkbox id="rm3" data-help-control="remember_me" checked={rememberMe} onCheckedChange={v => setRememberMe(!!v)}
                      className="dark:border-white/[0.2] border-gray-300 data-[state=checked]:bg-[#3DD56D] data-[state=checked]:border-[#3DD56D] h-3.5 w-3.5" />
                    <label htmlFor="rm3" className="text-xs dark:text-neutral-400 text-gray-500 cursor-pointer">{t('rememberMe')}</label>
                  </div>
                  <button type="button" onClick={() => setShowForgot(true)}
                    className="text-xs text-[#3DD56D] hover:text-[#5fe88a] transition-colors">{t('forgotPassword')}</button>
                </div>
                <Button type="submit" data-testid="login-submit-button" disabled={loading}
                  className="w-full h-9 rounded-lg bg-[#3DD56D] hover:bg-[#4ce07c] text-black font-semibold shadow-[0_4px_16px_rgba(61,213,109,0.22)] disabled:opacity-50 text-sm">
                  {loading
                    ? <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin" />{t('signingIn')}</span>
                    : <span className="flex items-center gap-1.5">{t('signInBtn')} <ArrowRight size={14} /></span>}
                </Button>
              </form>
              </FormHelp>

              {/* SSO Divider */}
              <div className="relative my-3">
                <Separator className="dark:bg-white/[0.07] bg-gray-100" />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 dark:bg-[#141414] bg-white px-2 text-[10px] dark:text-neutral-600 text-gray-400">or continue with</span>
              </div>

              {/* SSO Buttons */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { name: 'Google SSO', icon: '\uD83D\uDD35' },
                  { name: 'Microsoft SSO', icon: '\uD83D\uDFE6' },
                ].map(sso => (
                  <button key={sso.name} disabled
                    className="h-8 rounded-lg dark:bg-white/[0.04] bg-gray-50 dark:border-white/[0.08] border-gray-200 border flex items-center justify-center gap-1.5 dark:text-neutral-500 text-gray-400 cursor-not-allowed text-xs">
                    <Badge className="bg-transparent border-0 text-[9px] p-0 text-[#3DD56D]">Soon</Badge>
                    <span>{sso.name}</span>
                  </button>
                ))}
              </div>

              <Separator className="dark:bg-white/[0.07] bg-gray-100 mt-3 mb-2" />
              {/* "Request Access" hidden per product request
              <p className="text-center text-xs dark:text-neutral-500 text-gray-500">
                {t('newTo')}{' '}
                <a href="/signup" className="text-[#3DD56D] hover:text-[#5fe88a] font-medium">{t('requestAccess')}</a>
              </p>
              */}
            </div>

            {/* Demo hint */}
            <div className="mt-2 dark:bg-[#3DD56D]/[0.04] bg-[#3DD56D]/[0.04] border dark:border-[#3DD56D]/15 border-[#3DD56D]/15 rounded-lg px-3 py-2">
              <p className="text-[11px] dark:text-neutral-400 text-gray-500">
                <strong>Demo:</strong> admin@retailcliq.com / Admin@123 · demo / demo123
              </p>
            </div>
          </motion.div>
        </div>

        {/* RIGHT — Status Board (3 cols) */}
        <div className="hidden md:flex md:col-span-4 lg:col-span-3 flex-col p-3 relative overflow-hidden lg:min-h-screen dark:bg-[#0c0c0c] bg-slate-50">
          <div className="absolute bottom-0 right-0 w-full h-48 pointer-events-none"
            style={{ background: 'radial-gradient(280px circle at 110% 110%, rgba(61,213,109,0.08), transparent 60%)' }} />
          <div className="relative z-10 flex flex-col h-full gap-4">

            {/* Live clock */}
            <div className="dark:bg-white/[0.03] bg-white dark:border-white/[0.06] border-gray-100 border rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Clock size={11} className="text-[#3DD56D]" />
                <p className="text-[10px] font-semibold dark:text-neutral-500 text-gray-400 uppercase tracking-widest">Live Time</p>
              </div>
              <p className="text-2xl font-bold dark:text-white text-gray-900 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                {formatTime(time)}
              </p>
              <p className="text-[10px] dark:text-neutral-500 text-gray-400 mt-0.5">Asia/Kolkata (IST)</p>
            </div>

            {/* System status */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Server size={11} className="text-[#3DD56D]" />
                <p className="text-[10px] font-semibold dark:text-neutral-400 text-gray-500 uppercase tracking-widest">{t('systemStatus')}</p>
              </div>
              <div className="space-y-1.5">
                {STATUS_SERVICES.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 dark:bg-white/[0.03] bg-white dark:border-white/[0.05] border-gray-100 border rounded-lg px-2.5 py-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#3DD56D] flex-shrink-0" />
                    <s.icon size={11} className="dark:text-neutral-500 text-gray-400 flex-shrink-0" />
                    <span className="dark:text-neutral-300 text-gray-600 text-xs flex-1">{s.name}</span>
                    <span className="text-[10px] text-[#3DD56D] tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{s.latency}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-1.5 mt-1.5">
                <CheckCircle2 size={11} className="text-[#3DD56D]" />
                <span className="text-[11px] dark:text-neutral-400 text-gray-500">{t('allSystemsNormal')}</span>
              </div>
            </div>

            {/* Active users */}
            <div className="dark:bg-white/[0.03] bg-white dark:border-white/[0.06] border-gray-100 border rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Users size={11} className="dark:text-neutral-500 text-gray-400" />
                <p className="text-[10px] font-semibold dark:text-neutral-500 text-gray-400 uppercase tracking-widest">{t('activeUsers')}</p>
              </div>
              <p className="text-2xl font-bold dark:text-white text-gray-900 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{activeUsers}</p>
              <p className="text-[10px] dark:text-neutral-600 text-gray-400 mt-0.5">currently online</p>
            </div>

            {/* Uptime */}
            <div className="flex-1">
              <div className="flex items-center gap-1.5 mb-2">
                <BarChart3 size={11} className="dark:text-neutral-500 text-gray-400" />
                <p className="text-[10px] font-semibold dark:text-neutral-500 text-gray-400 uppercase tracking-widest">{t('uptime')} (30d)</p>
              </div>
              <div className="space-y-1.5">
                {[{ label: 'API', val: 99.9 }, { label: 'DB', val: 100 }, { label: 'POS', val: 99.7 }].map(u => (
                  <div key={u.label}>
                    <div className="flex justify-between text-[10px] mb-0.5">
                      <span className="dark:text-neutral-400 text-gray-500">{u.label}</span>
                      <span className="dark:text-neutral-400 text-gray-500 tabular-nums">{u.val}%</span>
                    </div>
                    <div className="h-1 rounded-full dark:bg-white/[0.06] bg-gray-200">
                      <div className="h-1 rounded-full bg-[#3DD56D]" style={{ width: `${u.val}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="dark:border-t dark:border-white/[0.06] border-t border-gray-100 pt-2">
              <p className="dark:text-neutral-700 text-gray-400 text-[10px]">v2.4.1 · © 2025 RetailCliQ</p>
            </div>
          </div>
        </div>

      </div>
      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
    </div>
  );
}
